<template>
  <div class="poll-vote">
    <div class="poll-vote__header">
      <v-icon
        x-small
        color="primary"
        class="mr-1"
      >
        mdi-poll
      </v-icon>
      <span class="poll-vote__question">{{ poll.question }}</span>
    </div>
    <div class="poll-vote__body">
      <!-- Single choice -->
      <v-radio-group
        v-if="poll.poll_type === 'single'"
        v-model="selectedOption"
        hide-details
        class="mt-0 pt-0"
      >
        <v-radio
          v-for="option in regularOptions"
          :key="option.id"
          :value="option.id"
          :label="option.text"
          class="poll-vote__option"
        />
        <template v-if="hasOtherOption">
          <v-radio
            :value="otherOption.id"
            :label="otherOption.text"
            class="poll-vote__option"
          />
          <v-text-field
            v-if="selectedOption === otherOption.id"
            v-model="otherText"
            placeholder="Please specify..."
            dense
            outlined
            hide-details
            class="mt-1 ml-7"
          />
        </template>
      </v-radio-group>

      <!-- Multi choice -->
      <template v-else>
        <v-checkbox
          v-for="option in regularOptions"
          :key="option.id"
          v-model="selectedOptions"
          :value="option.id"
          :label="option.text"
          hide-details
          dense
          class="mt-0 poll-vote__option"
        />
        <template v-if="hasOtherOption">
          <v-checkbox
            v-model="selectedOptions"
            :value="otherOption.id"
            :label="otherOption.text"
            hide-details
            dense
            class="mt-0 poll-vote__option"
          />
          <v-text-field
            v-if="selectedOptions.includes(otherOption.id)"
            v-model="otherText"
            placeholder="Please specify..."
            dense
            outlined
            hide-details
            class="mt-1 ml-7"
          />
        </template>
      </template>
    </div>
    <div class="poll-vote__actions">
      <button
        class="poll-vote__submit-btn"
        :class="{ 'poll-vote__submit-btn--done': submitted }"
        :disabled="!canSubmit || submitted || loading"
        @click="submit"
      >
        <v-icon
          x-small
          class="mr-1"
        >
          {{ submitted ? 'mdi-check-circle' : 'mdi-vote' }}
        </v-icon>
        {{ submitted ? 'Voted' : 'Vote' }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'PollVoteCard',

  props: {
    poll: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      selectedOption: null,
      selectedOptions: [],
      otherText: '',
      loading: false,
      submitted: false,
    };
  },

  computed: {
    regularOptions() {
      return (this.poll.options || []).filter((o) => !o.is_other);
    },

    otherOption() {
      return (this.poll.options || []).find((o) => o.is_other) || null;
    },

    hasOtherOption() {
      return !!this.otherOption;
    },

    canSubmit() {
      if (this.poll.poll_type === 'single') {
        return this.selectedOption !== null;
      }
      return this.selectedOptions.length > 0;
    },
  },

  methods: {
    ...mapActions('webinarChat', ['respondToPoll']),

    async submit() {
      if (!this.canSubmit || this.submitted || this.loading) return;
      this.loading = true;
      try {
        const optionIds = this.poll.poll_type === 'single'
          ? [this.selectedOption]
          : [...this.selectedOptions];

        await this.respondToPoll({
          pollId: this.poll.id,
          optionIds,
          otherText: this.otherText,
        });
        this.submitted = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:color";
.poll-vote {
  margin-bottom: 8px;
  border-radius: 10px;
  border: 1.5px solid rgba(244, 119, 33, 0.2);
  background: #fff;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: flex-start;
    padding: 10px 12px 6px;
    gap: 2px;
  }

  &__question {
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.8);
    line-height: 1.35;
  }

  &__body {
    padding: 0 12px 6px;

    ::v-deep .v-text-field .v-input__slot {
      min-height: 28px !important;
    }

    ::v-deep .v-text-field input {
      padding: 4px 8px;
      font-size: 12px;
    }
  }

  &__option {
    margin-bottom: 0 !important;

    ::v-deep .v-label {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.7);
    }

    ::v-deep .v-input--selection-controls__input {
      margin-right: 6px;
    }

    ::v-deep .v-icon {
      font-size: 18px !important;
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    padding: 4px 12px 10px;
  }

  &__submit-btn {
    display: inline-flex;
    align-items: center;
    padding: 5px 14px;
    border: none;
    border-radius: 6px;
    background: #f47721;
    color: #fff;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, opacity 0.15s ease;

    &:hover:not(:disabled) {
      background: color.adjust(#f47721, $lightness: -6%);
    }

    &:disabled {
      opacity: 0.45;
      cursor: default;
    }

    &--done {
      background: #43a047;
    }
  }
}
</style>
